import DomTranslator from './DomTranslator';
import LangPill from './LangPill';
import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../context/AuthContext';
import { canManageUsers, canManageSettings, getRoleLabel } from '../lib/utils';
import api from '../lib/api';
import { SANDBOX_SIDEBAR_PATHS } from '../lib/sandbox-access';
import KeystoneLogo from './KeystoneLogo';
import {
  LayoutDashboard,
  Truck,
  Users,
  Package,
  Layers,
  FileText,
  Settings,
  Search,
  LogOut,
  ChevronDown,
  Menu,
  X,
  Shield,
  ClipboardList,
  Scissors,
  CalendarDays,
  Tags,
  Palette,
  Trash2,
  ShieldCheck,
  Database,
  HardDrive,
  Barcode,
  FileSearch,
  Activity,
  ShoppingCart,
  ToggleLeft,
  ToggleRight,
  UserCog,
  Clock,
  ShieldAlert,
  Factory,
  PackageOpen,
  Droplets,
  Cylinder,
  FlaskConical,
  Rewind,
  FastForward,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Button } from './ui/button';

const ROLES_ALL = ['super_admin', 'admin', 'ops_user', 'operator', 'read_only'];
const ROLES_EDIT = ['super_admin', 'admin', 'ops_user', 'operator'];

const navSections = [
  {
    title: null, // first section unlabelled
    items: [
      { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: ROLES_ALL },
    ],
  },
  {
    title: 'Outwards',
    items: [
      { path: '/sales-order/new', label: 'Sales Orders', icon: ClipboardList, roles: ROLES_EDIT },
      { path: '/planner', label: 'Order Pipeline', icon: CalendarDays, roles: ROLES_ALL },
      { path: '/dispatches', label: 'Dispatches', icon: Truck, roles: ROLES_ALL },
      { path: '/customers', label: 'Customers', icon: Users, roles: ROLES_ALL },
      { path: '/supplier-codes', label: 'Customer Product Codes', icon: Barcode, roles: ROLES_ALL },
      { path: '/cobranding', label: 'Co-Branding', icon: Tags, roles: ROLES_ALL },
      { path: '/products', label: 'Products', icon: Package, roles: ROLES_ALL },
    ],
  },
  {
    title: 'Inwards',
    items: [
      { path: '/inwards/suppliers', label: 'Suppliers', icon: Factory, roles: ROLES_ALL },
      { path: '/inwards/supplier-codes', label: 'Supplier Product Codes', icon: Barcode, roles: ROLES_ALL },
      { path: '/inwards/deliveries', label: 'Inward Deliveries', icon: PackageOpen, roles: ROLES_ALL },
      { path: '/inwards/milk-intake', label: 'Milk Intake', icon: Droplets, roles: ROLES_ALL },
      { path: '/inwards/silos', label: 'Silos', icon: Cylinder, roles: ROLES_ALL },
    ],
  },
  {
    title: 'Production',
    items: [
      { path: '/production/make-days', label: 'Make Days', icon: FlaskConical, roles: ROLES_ALL },
      { path: '/batches', label: 'Cheese Batches', icon: Layers, roles: ROLES_ALL },
      { path: '/qc', label: 'QC Management', icon: ShieldCheck, roles: ['super_admin', 'admin'] },
    ],
  },
  {
    title: 'Traceability',
    items: [
      { path: '/traceability/backward', label: 'Backward Trace', icon: Rewind, roles: ROLES_ALL },
      { path: '/traceability/forward', label: 'Forward Trace', icon: FastForward, roles: ROLES_ALL },
      { path: '/traceability/recall', label: 'Mock Recall', icon: ShieldAlert, roles: ROLES_ALL },
      { path: '/traceability/events', label: 'Events', icon: FileText, roles: ROLES_ALL },
      { path: '/supplier-trace', label: 'Customer Code Trace', icon: FileSearch, roles: ROLES_ALL },
      { path: '/reports/make-day-summary', label: 'Reports', icon: FileText, roles: ROLES_ALL },
    ],
  },
];

// Legacy flat lists (kept for backward compat with hasPageAccess logic + header title lookup)
const navItems = navSections.flatMap(s => s.items);

const adminItems = [
  { path: '/users', label: 'User Management', icon: Shield, roles: ['super_admin', 'admin'] },
  { path: '/deleted-items', label: 'Deleted Items', icon: Trash2, roles: ['super_admin', 'admin'] },
  { path: '/admin/data-management', label: 'Data Management', icon: Database, roles: ['super_admin', 'admin'] },
  { path: '/backup', label: 'Backup & Restore', icon: HardDrive, roles: ['super_admin', 'admin'] },
  { path: '/admin/deployment', label: 'Deployment Health', icon: Activity, roles: ['super_admin', 'admin'] },
  { path: '/admin/compliance', label: 'Compliance Center', icon: ShieldCheck, roles: ['super_admin', 'admin', 'dpo'] },
  { path: '/admin/security-audit', label: 'Security Audit', icon: ShieldAlert, roles: ['super_admin', 'admin', 'dpo'] },
  { path: '/settings', label: 'Settings', icon: Settings, roles: ['super_admin', 'admin', 'operator'] },  // Operator can access settings for password change
];

export default function Layout({ children }) {
  // Runtime app translation layer (reads keystone_app_lang from localStorage)
  const { user, logout, sessionWarning, keepAlive } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  // Phase 3: collapsible sidebar sections, start with all sections open
  const [openSections, setOpenSections] = useState({
    Outwards: true, Inwards: true, Production: true, Traceability: true,
  });
  const [settings, setSettings] = useState(null);
  const [featureToggles, setFeatureToggles] = useState({});
  const [mfaBannerDismissed, setMfaBannerDismissed] = useState(() => {
    return sessionStorage.getItem('mfa_banner_dismissed') === 'true';
  });
  const [operatorMode, setOperatorMode] = useState(() => {
    return localStorage.getItem('operatorMode') === 'true';
  });

  useEffect(() => {
    loadSettings();
    loadFeatureToggles();
  }, []);

  const loadSettings = async () => {
    try {
      const response = await api.get('/settings');
      setSettings(response.data);
    } catch (error) {
      // Ignore - settings might not be accessible
    }
  };

  const loadFeatureToggles = async () => {
    try {
      const response = await api.get('/settings/feature-toggles');
      setFeatureToggles(response.data);
    } catch (error) {
      // Ignore
    }
  };

  const toggleOperatorMode = () => {
    const newVal = !operatorMode;
    setOperatorMode(newVal);
    localStorage.setItem('operatorMode', String(newVal));
    if (newVal) navigate('/quick-order');
  };

  // Operator mode nav items, streamlined for tablet operators
  const operatorModeNavItems = [
    { path: '/quick-order', label: 'Quick Order', icon: ShoppingCart, roles: ['super_admin', 'admin', 'operator'] },
    { path: '/planner', label: 'Order Pipeline', icon: CalendarDays, roles: ['super_admin', 'admin', 'operator'] },
    { path: '/account', label: 'My Account', icon: UserCog, roles: ['super_admin', 'admin', 'operator'] },
  ];

  // Dynamic nav items based on feature toggles
  const dynamicNavItems = featureToggles.transformations_enabled
    ? [...navItems.slice(0, navItems.findIndex(i => i.path === '/reports')),
       { path: '/transformations', label: 'Transformations', icon: Scissors, roles: ['super_admin', 'admin'] },
       ...navItems.slice(navItems.findIndex(i => i.path === '/reports'))]
    : navItems;

  // Map paths to page permission keys
  const pathToPermissionKey = {
    '/': 'dashboard',
    '/sales-order/new': 'sales_order_entry',
    '/planner': 'order_pipeline',
    '/dispatches': 'dispatches',
    '/dispatch-view': 'dispatch_view',
    '/dispatch/new': 'new_dispatch',
    '/customers': 'customers',
    '/cobranding': 'cobranding',
    '/products': 'products',
    '/supplier-codes': 'supplier_codes',
    '/supplier-trace': 'supplier_trace',
    '/batches': 'batches',
    '/qc': 'qc_management',
    '/templates': 'template_designer',
    '/reports': 'reports',
    '/users': 'user_management',
    '/deleted-items': 'deleted_items',
    '/admin/data-management': 'data_management',
    '/admin/deployment': 'deployment_health',
    '/backup': 'backup_restore',
    '/settings': 'settings',
    '/quick-order': 'quick_order',
    '/account': 'account',
  };

  // Check if user has access to a page based on role AND page_permissions
  const hasPageAccess = (item) => {
    // First check role-based access
    if (!item.roles.includes(user?.role)) return false;
    
    // Super admin and admin always have access (they can customize others' access)
    if (user?.role === 'super_admin' || user?.role === 'admin') return true;
    
    // For operators and read_only, check page_permissions if they exist
    const pagePermissions = user?.page_permissions || {};
    const permissionKey = pathToPermissionKey[item.path];
    
    // If no page_permissions set, allow access based on role (default behavior)
    if (Object.keys(pagePermissions).length === 0) return true;
    
    // If page_permissions exist, check if this page is allowed
    return permissionKey ? pagePermissions[permissionKey] === true : true;
  };

  const filteredNavItems = operatorMode
    ? operatorModeNavItems.filter(hasPageAccess)
    : dynamicNavItems.filter(hasPageAccess);
  const filteredAdminItems = operatorMode ? [] : adminItems.filter(hasPageAccess);

  // Public Sandbox detection, all menu items are visible, but non-allowed
  // routes render a "Demo Mode · Page available in full Keystone" placeholder
  // (handled at the routing layer in App.js + SandboxRestricted.jsx).
  const isSandbox = user?.is_sandbox || user?.email === 'demo@keystone.cloudvoro.com';
  const sandboxBlockedPaths = new Set(['/settings', '/users', '/audit-log', '/api-keys', '/role-management', '/data-management', '/security-audit', '/backups', '/db-management']);

  // Show ALL nav items for sandbox users (the route guard handles the actual
  // page block). This matches the user's request that menu items remain visible.
  const sandboxFilteredNavItems = filteredNavItems;
  const sandboxFilteredAdminItems = filteredAdminItems;

  const handleSandboxBlock = (e, path) => {
    if (isSandbox && sandboxBlockedPaths.has(path)) {
      e.preventDefault();
      toast.info('Unavailable in Public Sandbox', {
        description: 'This area is hidden in the demo. Sign up for a real account to configure settings, users and integrations.',
      });
      setSidebarOpen(false);
    }
  };

  return (
    <div className="flex flex-col min-h-screen bg-zinc-50">
      {isSandbox && (
        <div data-testid="sandbox-banner" className="w-full bg-pink-600 text-white text-center py-2 px-4 text-xs font-semibold tracking-wide flex items-center justify-center gap-3 flex-wrap">
          <span className="bg-white text-pink-600 px-2 py-0.5 rounded-sm uppercase tracking-widest text-[10px] font-bold">Public Sandbox · Read-only</span>
          <span>Explore freely with sample data, Jan–Aug 2026 Irish dairy operations. Saving, editing and deleting are disabled in demo mode.</span>
          <a href="/site/contact" className="underline font-bold hover:text-pink-100">Talk to us about a real account →</a>
        </div>
      )}
      <div className="flex flex-1">
      {/* Mobile sidebar overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed lg:static inset-y-0 left-0 z-50
        w-64 bg-zinc-900 text-white flex flex-col
        transform transition-transform duration-200
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
      `}>
        {/* Logo */}
        <div className="flex items-center justify-between h-16 px-4 border-b border-zinc-800">
          <Link to="/app" className="flex items-center gap-2 min-w-0">
            {settings?.company_logo ? (
              <img 
                src={settings.company_logo} 
                alt={settings?.company_name || 'Logo'} 
                className="h-8 w-auto max-w-[120px] object-contain"
              />
            ) : (
              <KeystoneLogo height={32} variant="dark" showTagline={false} />
            )}
          </Link>
          <button 
            className="lg:hidden p-1 hover:bg-zinc-800 rounded flex-shrink-0"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 py-4 overflow-y-auto">
          {operatorMode ? (
            <div className="space-y-1 px-2">
              {sandboxFilteredNavItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={(e) => { handleSandboxBlock(e, item.path); setSidebarOpen(false); }}
                    className={`sidebar-item ${isActive ? 'active' : ''} ${isSandbox && sandboxBlockedPaths.has(item.path) ? 'opacity-60' : ''}`}
                    data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                    <span className="truncate">{item.label}</span>
                  </Link>
                );
              })}
            </div>
          ) : (
            <>
              {navSections.map((section, idx) => {
                const visible = section.items.filter(hasPageAccess);
                if (!visible.length) return null;
                // Unlabelled section (Dashboard), never collapsible
                if (!section.title) {
                  return (
                    <div key={idx} className={idx > 0 ? 'mt-4' : ''}>
                      <div className="space-y-1 px-2">
                        {visible.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={(e) => { handleSandboxBlock(e, item.path); setSidebarOpen(false); }}
                              className={`sidebar-item ${isActive ? 'active' : ''} ${isSandbox && sandboxBlockedPaths.has(item.path) ? 'opacity-60' : ''}`}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                              <span className="truncate">{item.label}</span>
                            </Link>
                          );
                        })}
                      </div>
                    </div>
                  );
                }
                // Collapsible labelled section
                const sectionKey = section.title;
                const containsActive = visible.some(it => location.pathname === it.path || location.pathname.startsWith(it.path + '/'));
                const isOpen = openSections[sectionKey] ?? containsActive;
                return (
                  <div key={idx} className={idx > 0 ? 'mt-3' : ''} data-testid={`nav-section-${sectionKey.toLowerCase()}`}>
                    <button
                      type="button"
                      onClick={() => setOpenSections(s => ({ ...s, [sectionKey]: !isOpen }))}
                      className="w-full flex items-center justify-between px-4 py-1.5 group hover:bg-zinc-800/50 rounded-md mx-2 transition-colors"
                      data-testid={`nav-section-toggle-${sectionKey.toLowerCase()}`}
                    >
                      <span className="text-[10px] font-semibold text-zinc-400 uppercase tracking-[0.15em] group-hover:text-zinc-200">
                        {section.title}
                      </span>
                      <ChevronDown className={`w-3.5 h-3.5 text-zinc-500 transition-transform ${isOpen ? '' : '-rotate-90'}`} />
                    </button>
                    {isOpen && (
                      <div className="space-y-1 px-2 mt-1">
                        {visible.map((item) => {
                          const Icon = item.icon;
                          const isActive = location.pathname === item.path;
                          return (
                            <Link
                              key={item.path}
                              to={item.path}
                              onClick={(e) => { handleSandboxBlock(e, item.path); setSidebarOpen(false); }}
                              className={`sidebar-item ${isActive ? 'active' : ''} ${isSandbox && sandboxBlockedPaths.has(item.path) ? 'opacity-60' : ''}`}
                              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                            >
                              <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                              <span className="truncate">{item.label}</span>
                              {isSandbox && sandboxBlockedPaths.has(item.path) && (
                                <span className="ml-auto text-[9px] font-bold tracking-wider uppercase text-pink-400">Demo</span>
                              )}
                            </Link>
                          );
                        })}
                      </div>
                    )}
                  </div>
                );
              })}
            </>
          )}

          {sandboxFilteredAdminItems.length > 0 && (
            <>
              <div className="px-4 py-3 mt-4">
                <span className="text-xs font-medium text-zinc-500 uppercase tracking-wider">
                  Administration
                </span>
              </div>
              <div className="space-y-1 px-2">
                {sandboxFilteredAdminItems.map((item) => {
                  const Icon = item.icon;
                  const isActive = location.pathname === item.path;
                  return (
                    <Link
                      key={item.path}
                      to={item.path}
                      onClick={(e) => { handleSandboxBlock(e, item.path); setSidebarOpen(false); }}
                      className={`sidebar-item ${isActive ? 'active' : ''} ${isSandbox && sandboxBlockedPaths.has(item.path) ? 'opacity-60' : ''}`}
                      data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                    >
                      <Icon className="w-5 h-5 flex-shrink-0" strokeWidth={1.5} />
                      <span className="truncate">{item.label}</span>
                      {isSandbox && sandboxBlockedPaths.has(item.path) && (
                        <span className="ml-auto text-[9px] font-bold tracking-wider uppercase text-pink-400">Demo</span>
                      )}
                    </Link>
                  );
                })}
              </div>
            </>
          )}
        </nav>

        {/* Operator Mode Toggle */}
        <div className="px-3 py-2 border-t border-zinc-800">
          <button
            onClick={toggleOperatorMode}
            className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-sm transition-colors ${
              operatorMode 
                ? 'bg-primary/20 text-primary hover:bg-primary/30' 
                : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
            }`}
            data-testid="btn-operator-mode-toggle"
          >
            {operatorMode ? <ToggleRight className="w-5 h-5 flex-shrink-0" /> : <ToggleLeft className="w-5 h-5 flex-shrink-0" />}
            <span className="truncate">Operator Mode</span>
          </button>
        </div>

        {/* User section */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3">
            <div className="w-8 h-8 bg-zinc-700 rounded-full flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-medium">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium truncate">{user?.name}</p>
              <p className="text-xs text-zinc-400 truncate">{getRoleLabel(user?.role)}</p>
            </div>
          </div>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-14 sm:h-16 bg-white border-b border-border flex items-center justify-between px-3 sm:px-6 sticky top-0 z-30">
          <div className="flex items-center gap-2 sm:gap-4 min-w-0">
            <button 
              className="lg:hidden p-2 hover:bg-zinc-100 rounded flex-shrink-0"
              onClick={() => setSidebarOpen(true)}
            >
              <Menu className="w-5 h-5" />
            </button>
            <h1 className="text-base sm:text-lg font-semibold text-foreground truncate">
              {filteredNavItems.find(i => i.path === location.pathname)?.label || 
               filteredAdminItems.find(i => i.path === location.pathname)?.label ||
               (settings?.company_name || 'Operations Hub')}
            </h1>
          </div>

          <div className="flex items-center gap-2">
            <div className="hidden sm:block"><LangPill variant="subtle" /></div>
            <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-1 sm:gap-2 px-2 sm:px-4" data-testid="user-menu">
                <span className="hidden sm:inline truncate max-w-[150px]">{user?.email}</span>
                <span className="sm:hidden">{user?.name?.charAt(0).toUpperCase() || 'U'}</span>
                <ChevronDown className="w-4 h-4 flex-shrink-0" />
              </Button>
            </DropdownMenuTrigger>            <DropdownMenuContent align="end" className="w-56">
              <div className="px-2 py-1.5">
                <p className="text-sm font-medium truncate">{user?.name}</p>
                <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
              </div>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate('/profile')} data-testid="menu-profile">
                Profile & Security
              </DropdownMenuItem>
              {canManageSettings(user?.role) && (
                <DropdownMenuItem onClick={() => navigate('/settings')} data-testid="menu-settings">
                  Settings
                </DropdownMenuItem>
              )}
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={logout} className="text-red-600" data-testid="menu-logout">
                <LogOut className="w-4 h-4 mr-2" />
                Sign Out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          </div>
        </header>

        {/* Session expiry warning banner */}
        {sessionWarning && (
          <div data-testid="session-warning-banner" className="bg-amber-500 text-white px-4 py-2 flex items-center justify-between text-sm">
            <div className="flex items-center gap-2">
              <Clock className="w-4 h-4" />
              <span>Your session will expire in 2 minutes due to inactivity.</span>
            </div>
            <button onClick={keepAlive} className="bg-white/20 hover:bg-white/30 px-3 py-1 rounded text-xs font-medium" data-testid="keep-session-btn">
              Stay Logged In
            </button>
          </div>
        )}

        {/* MFA reminder banner, soft, dismissible per session */}
        {user && !user.mfa_enabled && !mfaBannerDismissed && (
          <div data-testid="mfa-reminder-banner" className={`${user.mfa_setup_pending ? 'bg-amber-600' : 'bg-sky-600'} text-white px-4 py-2 flex items-center justify-between text-sm`}>
            <div className="flex items-center gap-2">
              <Shield className="w-4 h-4" />
              {user.mfa_setup_pending ? (
                <span><strong>MFA setup incomplete.</strong> Your previous setup was not verified. <button onClick={() => navigate('/profile')} className="underline font-medium">Complete it now</button> to secure your account.</span>
              ) : (
                <span>Two-factor authentication is not enabled on your account. <button onClick={() => navigate('/profile')} className="underline font-medium">Set it up now</button> for better security.</span>
              )}
            </div>
            <button
              onClick={() => { setMfaBannerDismissed(true); sessionStorage.setItem('mfa_banner_dismissed', 'true'); }}
              className="hover:bg-white/20 p-1 rounded"
              data-testid="dismiss-mfa-banner"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        )}

        {/* Page content */}
        <main className="flex-1 p-3 sm:p-4 lg:p-6 overflow-auto">
          <DomTranslator />
          {children}
        </main>
      </div>
      </div>
    </div>
  );
}
